'use client'
import { useEffect, useState } from 'react'
import type { OrderAddressDTO } from 'ecosystem'
import { useUser } from 'auth'
import { useRememberedAddress } from '../../../hooks'
import {
  companyAddressToOrderAddress,
  pickDefaultBillingAddress,
  resolveRememberedChoice
} from './companyAddressToOrderAddress'
import { type OrderAddressWithContact } from './CustomAddressForm'

/**
 * Tracks the billing address used by a DEFAULT-shipping flow (invoice or
 * upfront). Seeds from `customer.companyProfile.addresses` (the default
 * BILLING address) on first render, falls back to the caller-supplied
 * `persisted` address if present, and exposes `setBillingAddress` so the
 * parent can pipe it into a `<BillingAddressPicker>`.
 *
 * Seeding order, most authoritative first: the order, then the choice this device
 * remembers, then the company's default BILLING address.
 *
 * The remembered choice sits *above* the company default on purpose. A default is a
 * guess at what the shopper wants; a remembered choice is what they actually picked here
 * last time, which is the more specific answer. That is also the only way a B2B shopper
 * who picks the custom option ever sees it again — the company default would otherwise
 * win on every subsequent checkout and the offer to remember would be empty words.
 * A remembered *company* choice is resolved by id against the live address list, so it
 * reorders the account's own addresses rather than overriding the account.
 *
 * The persisted source is passed in so the hook stays decoupled from
 * whichever order response (invoice DRAFT vs upfront) is in play.
 *
 * `email` rides along for flows that ask the address form for contact details (see
 * `requireContactDetails`); it is seeded from the signed-in account and stays editable,
 * since the shopper may want the confirmation sent somewhere else.
 */
export const useBillingAddress = (persisted: OrderAddressDTO | undefined) => {
  const { user } = useUser()
  const customerEmail = user?.customer?.email
  const { remembered, isReady } = useRememberedAddress('billing')

  // Seeded entirely by the effect below. Nothing is chosen eagerly here: reading the
  // company default on the first render would install it before the device has been
  // read, and the effect then bails out because something is already selected — which is
  // exactly how a remembered choice used to get lost whenever `useUser` was warm.
  const [billingAddress, setBillingAddress] = useState<OrderAddressWithContact | undefined>(
    undefined
  )

  // Single seeding chain, running once something to seed from arrives — `persisted`,
  // the device read, or the late-hydrating `companyProfile` addresses.
  //
  // `remembered` resolves off the same `useUser` call as the company addresses, so by
  // the time a choice is readable the list it is resolved against is loaded too; a
  // remembered company id can never miss because its address had not arrived yet.
  // Selecting here (rather than only prefilling the form) is what lets the picker show
  // the address folded — and keeps that display honest, since it is then the address the
  // order actually carries.
  useEffect(() => {
    if (billingAddress) return

    // The order knows where it is going; nothing outranks it, and it needs no device.
    if (persisted) {
      setBillingAddress(persisted)
      return
    }

    // Below here every branch competes with the device, so wait until it has been read.
    if (!isReady) return

    const companyAddresses = user?.customer?.companyProfile?.addresses
    const chosen = resolveRememberedChoice(remembered, companyAddresses)
    if (chosen) {
      setBillingAddress(chosen)
      return
    }

    const company = pickDefaultBillingAddress(companyAddresses ?? [])
    if (company) setBillingAddress(companyAddressToOrderAddress(company))
  }, [billingAddress, isReady, persisted, remembered, user?.customer?.companyProfile?.addresses])

  // Prefill the contact email once the account resolves, without clobbering a value the
  // shopper has already typed.
  useEffect(() => {
    if (!customerEmail) return
    setBillingAddress((current) =>
      current && !current.email ? { ...current, email: customerEmail } : current
    )
  }, [customerEmail])

  return { billingAddress, setBillingAddress }
}
